// Pure math behind the swap panel's confirmation surface.
//
// These numbers are the ones a user approves with a signature, so they live
// outside the component where they can be tested on their own: what the trade
// gives up against the depth-free mid price, how that reads in dollars, and
// whether the live quote has drifted away from what the confirm block showed.

import type { Quote } from './amm'

/// Output surrendered to pool depth: the gap between the depth-free mid-price
/// output and what the route actually returns. Never negative — a route that
/// beats mid (rounding) costs nothing.
export function impactShortfall(quote: Quote): bigint {
	const gap = quote.spotOut - quote.amountOut
	return gap > 0n ? gap : 0n
}

/// `impactShortfall` valued in dollars through a caller-supplied valuation of
/// the OUTPUT token. Null when that token is unpriceable — a confirmation
/// screen may not invent a number.
export function impactCostUsd(
	quote: Quote,
	valueOf: (raw: bigint) => number | null
): number | null {
	const gap = impactShortfall(quote)
	if (gap <= 0n) {
		return 0
	}
	return valueOf(gap)
}

/// Fixed-2dp dollars. Sub-cent amounts are named rather than rounded to
/// "$0.00", which reads as free.
export function formatUsd(value: number): string {
	const abs = Math.abs(value)
	if (abs > 0 && abs < 0.005) {
		return '<$0.01'
	}
	return `$${value.toLocaleString('en-US', {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2
	})}`
}

/// Ceiling on the input the user accepted. Exact-in trades sign a fixed input
/// so this is a no-op for them; exact-out trades let the input float, and the
/// slippage tolerance is the only budget it may float inside.
export function maxInput(amountIn: bigint, slippagePct: number): bigint {
	const bps = BigInt(Math.round(slippagePct * 100))
	return amountIn + (amountIn * bps) / 10_000n
}

/// Has the live quote moved outside what the confirm block showed? Either the
/// output no longer clears the minimum received, or the trade now costs more
/// input than the reviewed ceiling. Either way the user must look again — the
/// alternative is signing a trade they never saw.
export function quoteMoved(
	live: { amountIn: bigint; amountOut: bigint },
	reviewed: { minOut: bigint; maxIn: bigint }
): boolean {
	return live.amountOut < reviewed.minOut || live.amountIn > reviewed.maxIn
}
